/**
 * DIALOG: Libérer un numéro d'équipage orphelin (TICKET-CRM-07).
 *
 * Action irréversible côté numéro : nullifie crews.crew_number. L'équipage
 * RESTE actif et pourra relancer un paiement ultérieurement. Raison obligatoire
 * (audit trail). Affiche la ligne ciblée en clair pour éviter les erreurs.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui';
import type { OrphanCrewNumber } from '@/types/orphanCrewNumber';
import { memberLabel } from '../../lib/orphanCrewNumberErrors';

const releaseOrphanSchema = z.object({
  reason: z.string().trim().min(1, 'Raison obligatoire').max(500, '500 caractères maximum'),
});

type ReleaseOrphanForm = z.infer<typeof releaseOrphanSchema>;

interface ReleaseOrphanCrewNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orphan: OrphanCrewNumber | null;
  onConfirm: (crewId: string, reason: string) => Promise<void>;
}

export function ReleaseOrphanCrewNumberDialog({
  open,
  onOpenChange,
  orphan,
  onConfirm,
}: ReleaseOrphanCrewNumberDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReleaseOrphanForm>({
    resolver: zodResolver(releaseOrphanSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) {
      reset({ reason: '' });
    }
  }, [open, reset]);

  if (!orphan) return null;

  const productLabel = orphan.millesime
    ? `${orphan.product_name} ${orphan.millesime}`
    : orphan.product_name;

  const onSubmit = handleSubmit(async (values) => {
    await onConfirm(orphan.crew_id, values.reason);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Libérer le numéro d'équipage</DialogTitle>
          <DialogDescription>
            L'équipage reste actif et pourra relancer un paiement ultérieurement. Seul le numéro
            d'équipage est libéré.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-zinc-500">N° équipage</span>
            <span className="font-mono font-semibold text-zinc-900">{orphan.crew_number}</span>
          </div>
          <div className="mt-1 flex justify-between gap-4">
            <span className="text-zinc-500">Pilote</span>
            <span className="text-zinc-900">{memberLabel(orphan.pilot)}</span>
          </div>
          <div className="mt-1 flex justify-between gap-4">
            <span className="text-zinc-500">Produit</span>
            <span className="text-zinc-900">{productLabel}</span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="release-reason"
              className="mb-1 block text-sm font-medium text-zinc-700"
            >
              Raison (obligatoire)
            </label>
            <textarea
              id="release-reason"
              rows={3}
              placeholder="Ex : abandon confirmé, doublon, erreur de saisie…"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm shadow-sm placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
              {...register('reason')}
            />
            {errors.reason && <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" variant="destructive" disabled={isSubmitting}>
              {isSubmitting ? 'Libération…' : 'Libérer le numéro'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
